import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pill, Save, ShieldCheck, Plus, Trash2 } from "lucide-react";

export default function AdminPharmacies() {
  const [phs, setPhs] = useState([]);
  const [sid, setSid] = useState("");
  const [form, setForm] = useState({});
  const [cat, setCat] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/pharmacies");
    setPhs(data);
    if (data.length && !sid) setSid(data[0].id);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const p = phs.find((x) => x.id === sid);
    if (!p) return;
    setForm({
      name: p.name || "", address: p.address || "", phone: p.phone || "",
      rating: p.rating || 4.4, delivery_time_min: p.delivery_time_min || 45,
    });
    setCat((p.catalogue || []).map((c) => ({ ...c })));
  }, [sid, phs]);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const addRow = () => setCat([...cat, { name: "", price: 0 }]);
  const updRow = (i, k, v) => setCat(cat.map((c, j) => j === i ? { ...c, [k]: k === "price" ? Number(v) : v } : c));
  const rmRow = (i) => setCat(cat.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      const clean = cat.filter((c) => c.name.trim()).map((c) => ({ name: c.name.trim(), price: Number(c.price) || 0 }));
      await api.patch(`/admin/pharmacies/${sid}`, {
        name: form.name, address: form.address, phone: form.phone,
        rating: Number(form.rating),
        delivery_time_min: Number(form.delivery_time_min),
        catalogue: clean,
      });
      toast.success("Pharmacy updated"); load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 pb-24 sm:pb-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-amber-500/15 text-amber-700 flex items-center justify-center"><Pill className="w-5 h-5" /></div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-700">Admin · Pharmacies</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Manage pharmacy details</h1>
        </div>
      </div>

      <Card className="p-4 rounded-2xl mb-4">
        <Label>Choose pharmacy</Label>
        <Select value={sid} onValueChange={setSid}>
          <SelectTrigger data-testid="admin-pharm-select"><SelectValue /></SelectTrigger>
          <SelectContent>{phs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </Card>

      <Card className="p-5 rounded-2xl space-y-3">
        <div><Label>Name</Label><Input data-testid="p-name" value={form.name || ""} onChange={set("name")} /></div>
        <div><Label>Address</Label><Input data-testid="p-address" value={form.address || ""} onChange={set("address")} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Phone</Label><Input data-testid="p-phone" value={form.phone || ""} onChange={set("phone")} /></div>
          <div><Label>Rating</Label><Input data-testid="p-rating" type="number" step="0.1" value={form.rating || ""} onChange={set("rating")} /></div>
        </div>
        <div><Label>Delivery ETA (minutes)</Label><Input data-testid="p-eta" type="number" value={form.delivery_time_min || 0} onChange={set("delivery_time_min")} /></div>
      </Card>

      <Card className="p-5 rounded-2xl mt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Medicine catalogue</div>
          <Button data-testid="p-add" size="sm" variant="outline" onClick={addRow}><Plus className="w-4 h-4 mr-1" /> Add medicine</Button>
        </div>
        <div className="space-y-2">
          {cat.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No medicines yet — click "Add medicine".</div>}
          {cat.map((c, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`cat-row-${i}`}>
              <Input className="flex-1" placeholder="Medicine name" value={c.name} onChange={(e) => updRow(i, "name", e.target.value)} data-testid={`cat-name-${i}`} />
              <Input className="w-28" type="number" placeholder="₹ price" value={c.price} onChange={(e) => updRow(i, "price", e.target.value)} data-testid={`cat-price-${i}`} />
              <Button variant="ghost" size="icon" onClick={() => rmRow(i)} data-testid={`cat-rm-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}
        </div>
      </Card>

      <div className="mt-6 flex items-center justify-end gap-3">
        <div className="text-xs text-slate-500 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Admin-only actions</div>
        <Button data-testid="admin-save-pharm" onClick={save} disabled={saving || !sid} className="bg-amber-600 hover:bg-amber-500 text-white">
          <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
